import { PatientLocationHistory } from '../patients/patient-location-history.model';
import { PatientPhone } from '../patients/patient-phone.model';
import { AddressUse } from '../patients/address';
import { CartRxData } from './add-items-models';
import { patientDisplayName } from '../../shared/utility';
import { DispatchOrderReturn } from './order-return.model';

/** OrderReason::PolicyChangeInsuranceSwitch (DOVE-2061). */
export const INSURANCE_SWITCH_REASON_ID = 22;

/** DOVE-2584 — order types the duplicate-order warning applies to; the endpoint 422s for any other. */
export const DUPLICATE_ORDER_CHECK_TYPES: Record<number, string> = { 1: 'Delivery', 5: 'Pickup' };

/** DOVE-2584 — one row of GET /patient/{patient}/duplicate-orders. */
export interface IDuplicateOrder {
	id: number;
	scheduled_at: string;
}

/** DOVE-2584 — Add Order modal warning: matches plus the type label and MM/DD/YYYY date they were found for. */
export interface IDuplicateOrderWarning {
	orders: IDuplicateOrder[];
	orderType: string;
	date: string;
}

/**
 * DOVE-2112 — order sources for which the "Ordered By" field renders the user
 * dropdown (+ "User not found" fallback). All other sources keep free text.
 * Online Order=9, Call In - Hospice=12, Weekly Request=8, Email=2.
 */
export const ORDERED_BY_APPLICABLE_SOURCES = [9, 12, 8, 2];

/**
 * DOVE-2112 — one row of GET /orders/ordered-by-options. The API only returns Company/Client;
 * `Current` (DOVE-2599) is set client-side on an order's saved user who is no longer in the list.
 */
export interface IOrderedByOption {
	value: number;
	label: string;
	email: string;
	group: 'Company' | 'Client' | 'Current';
}

/** DOVE-2599 — display-only suffix; neutral because the cause may be lost insurance access or a deactivated account. */
export const ORDERED_BY_UNAVAILABLE_SUFFIX = ' (no longer available)';

/**
 * DOVE-2112 QA — grouped ("Company Users" / "Client Users") options for the
 * "Ordered By" p-dropdown. Shape matches PrimeNG SelectItemGroup so it can be
 * bound directly to `[options]` with `[group]="true"` (items carry the extra
 * `email` field so `filterBy="label,email"` can search name + email).
 */
export interface IOrderedByGroup {
	label: string;
	value: IOrderedByOption['group'];
	items: IOrderedByOption[];
}

/** Placeholder shown on the disabled "Ordered By" dropdown before an Order Source is picked. */
export const ORDERED_BY_DISABLED_PLACEHOLDER = 'Select Order Source first';

/**
 * DOVE-2387 — a branch-to-branch transfer needs a driver at each end, so the dispatch
 * transfer feeds can be expanded into one row per assignable leg (`legs=1`). `sending` is
 * the leg the origin branch's driver runs, `receiving` the one the destination's driver runs.
 */
export type TransferLeg = 'sending' | 'receiving';

/** DOVE-2387 — one entry of the `assignments` payload the transfer bulk endpoints take. */
export interface ITransferLegAssignment {
	transfer_order_id: number;
	leg: TransferLeg;
}

/** Partition flat options into non-empty Current/Client/Company SelectItemGroups for the grouped p-dropdown. */
export function buildOrderedByGroups(options: IOrderedByOption[]): IOrderedByGroup[] {
	const current = options.filter((o) => o.group === 'Current');
	const company = options.filter((o) => o.group === 'Company');
	const client = options.filter((o) => o.group === 'Client');
	const groups: IOrderedByGroup[] = [];
	if (current.length) {
		groups.push({ label: 'Current selection', value: 'Current', items: current });
	}
	if (client.length) {
		groups.push({ label: 'Client Users', value: 'Client', items: client });
	}
	if (company.length) {
		groups.push({ label: 'Company Users', value: 'Company', items: company });
	}
	return groups;
}

export type OrderedByTag = Exclude<IOrderedByOption['group'], 'Current'>;
/** DOVE-2406 — the "Ordered By" panel's filter tags, in display order. */
export const ORDERED_BY_TAGS: OrderedByTag[] = ['Client', 'Company'];

/**
 * DOVE-2406 — narrows the grouped options to the active tag. The selected option is kept even
 * when it sits in the other group: p-dropdown resolves its label from `[options]`, so dropping
 * it would blank the field while the tag is on. The `Current` group (DOVE-2599) is never
 * narrowed: it is the order's saved value, not a Client/Company listing.
 */
export function filterOrderedByGroups(
	groups: IOrderedByGroup[],
	activeTag: OrderedByTag | null,
	selectedValue: number | null | undefined
): IOrderedByGroup[] {
	if (!activeTag) {
		return groups;
	}
	return groups
		.map((group) =>
			group.value === activeTag || group.value === 'Current'
				? group
				: { ...group, items: group.items.filter((option) => option.value === selectedValue) }
		)
		.filter((group) => group.items.length > 0);
}

export interface IOrder {
	orderId: number;
	patientId: number;
	orderFlagName?: string;
	orderFlagIcon?: string;
	orderFlagId?: number;
	branchName: string;
	patientName: string;
	patient_location: string;
	patientActive: string;
	//>>>>Temporary removed for find usage: orderDate: Date;
	orderType: string;
	orderStatus: string;
	orderStatusName?: string;
	viewedBy?: { id: number; name: string } | null;
	dispatchTrackerStatus: string | null;
	/** DOVE-2330 — status id, so gates compare enum values instead of display strings. */
	dispatchTrackerStatusId: number | null;
	dispatchTrackerStatusHistory: IDispatchTrackerStatusHistory[];
	assignment: number;
	driverName: string;
	driverNameIni: string;
	insuranceName: string;
	currentAddress: string;
	currentCity: string;
	currentCounty?: string;
	currentState: string;
	currentZipcode: string;
	currentFullAddress: string;
	currentLatLong: string;
	createdByCustomer?: number;
	orderItemList?: IOrderItem[];
	deliveryWindowId: number;
	deliveryWindow?: string;
	customDeliveryWindowStart?: number;
	customDeliveryWindowEnd?: number;
	isTransferOrder?: boolean;
	/** DOVE-2387 — set only on transfer rows returned by the leg-expanded (`legs=1`) feeds. */
	leg?: TransferLeg;
	legKey?: string;
	legBranchId?: number | null;
	legBranchName?: string;
	/** DOVE-1743 — driver ETA; non-null only while the order is In-Route. */
	eta?: IOrderEta | null;
	/** DOVE-2667 — the driver's return on a Refused/Rescheduled tile; null otherwise. */
	return?: DispatchOrderReturn | null;
}

/** DOVE-1743 — `eta` block on the dispatch order feeds, mapped from the API's snake_case keys in the Order constructor. */
export interface IOrderEta {
	etaMinutes: number;
	etaArrivalAt: string;
	distanceMeters: number | null;
	updatedAt: string;
}

export interface IOrderItem {
	orderDetailId: number;
	thumbnail: string;
	imgMid: string;
	imgFull: string;
	displayProductCategoryName: string;
	displayProductName: string;
	quantity: number;
	dateReceived: string;
	itemMasterId: number;
}

export class Order implements IOrder {
	orderId: number; // Not in orderBe
	patientId: number;
	orderFlagName?: string; // Not in orderBe
	orderFlagIcon?: string; // Not in orderBe
	orderFlagId?: number; // Not in orderBe
	branchName: string; // Not in orderBe
	patientName: string; // Not in orderBe
	patient_location: string;
	patientActive: string; // Not in orderBe

	//>>>>Temporary removed for find usage:  orderDate: Date; // Maps to 'scheduled_date' in orderBe [NOTE: This is currently used, but should be replace with createdAt below]
	//following are all dates in database
	closedAt: Date;
	completedAt: Date;
	confirmedAt: Date;
	createdAt: Date;
	scheduledAt: Date;
	signedOffAt: Date;
	updatedAt: Date;
	// startTime: Date;
	// endTime: Date;

	orderType: string; // Not in orderBe
	orderStatus: string; // Not in orderBe
	orderStatusName?: string; // Order status name string (e.g. "Received"), used by the dispatcher map
	viewedBy?: { id: number; name: string } | null; // Present when a pipeline order has been viewed
	dispatchTrackerStatus: string;
	/** DOVE-2330 — id of the dispatch tracker status; string labels are display-only. */
	dispatchTrackerStatusId: number | null;
	dispatchTrackerStatusHistory: IDispatchTrackerStatusHistory[];
	assignment: number; // Not in orderBe
	driverName: string; // Not in orderBe
	driverNameIni: string; // Not in orderBe
	insuranceName: string; // Not in orderBe
	currentAddress: string; // Not in orderBe
	currentCity: string; // Not in orderBe
	currentCounty?: string; // Not in orderBe
	currentState: string; // Not in orderBe
	currentZipcode: string; // Not in orderBe
	currentFullAddress: string; // Not in orderBe
	currentLatLong: string; // Not in orderBe
	createdByCustomer?: number; // Not in orderBe
	orderItemList?: IOrderItem[]; // Not in orderBe
	deliveryWindowId: number; // Maps to 'delivery_window_id' in orderBe
	deliveryWindow?: string; // Not in orderBe
	customDeliveryWindowStart?: number; // Maps to 'custom_delivery_window_start' in orderBe
	customDeliveryWindowEnd?: number; // Maps to 'custom_delivery_window_end' in orderBe
	isTransferOrder?: boolean; // Flag to identify transfer orders in dispatcher
	/**
	 * DOVE-2387 — leg identity for transfer rows. `legKey` ("552:receiving") is the stable
	 * unique row key: a Handoff produces two rows carrying the SAME `orderId`, so anything
	 * keyed on the order id alone (selection, DOM lookups, marker maps) collides.
	 */
	leg?: TransferLeg;
	legKey?: string;
	legBranchId?: number | null;
	legBranchName?: string;
	eta: IOrderEta | null;
	/** DOVE-2667 — `return` block of DispatchOrderResource; null unless the order is 17/18. */
	return: DispatchOrderReturn | null;

	//########## CONSTRUCTOR ###############
	constructor(data: any) {
		this.orderId = data.orderId || data.order_id || data.id || 0;

		// this.patientId = data.patientId || data.patient_id || 0;
		this.patientId = data.patient ? `${data.patient.id}` : data.patientId || data.patient_id || '';

		this.orderFlagName = data.orderFlagName || data.order_flag_name || null;
		this.orderFlagIcon = data.orderFlagIcon || data.order_flag_icon || null;
		this.orderFlagId = data.orderFlagId || data.order_flag_id || null;

		//this.branchName = data.branchName || data.branch_name || '';
		this.branchName = data.branch?.name || data.branch?.branch_name || data.branchName || data.branch_name || '';

		//this.patientName = data.patientName || data.patient_name || '';
		// DOVE-2586 — a hospice house has no first name, so skip missing parts.
		this.patientName = data.patient
			? patientDisplayName(data.patient.first_name, data.patient.last_name)
			: data.patientName || data.patient_name || '';

		this.patient_location = data.patient_location || '';

		this.patientActive = data.patientActive || data.patient_active || '';
		//>>>>Temporary removed for find usage:  this.orderDate = new Date(data.orderDate || data.scheduled_date || new Date());

		this.closedAt = new Date(data.closedAt || data.closed_at || new Date());
		this.completedAt = new Date(data.completedAt || data.completed_at || new Date());
		this.confirmedAt = new Date(data.confirmedAt || data.confirmed_at || new Date());
		this.createdAt = new Date(data.createdAt || data.created_at || new Date()); //it was < data.orderDate || data.scheduled_date > before, any reference should be updated
		this.scheduledAt = new Date(data.scheduledAt || data.scheduled_at || new Date());
		this.signedOffAt = new Date(data.signedOffAt || data.signed_off_at || new Date());
		this.updatedAt = new Date(data.updatedAt || data.updated_at || new Date());
		// this.startTime: Date;
		// this.endTime: Date;

		// this.orderType = data.orderType || data.order_type?.split(' ')[0] || ''; // filter out 'Ticket'
		this.orderType = data.orderType?.type || data.order_type?.type; //?.split(' ')[0] || ''; // filter out 'Ticket'

		this.orderStatus = data.orderStatus || data.order_status || '';
		// order_status arrives as an object ({id, status}) on the dispatch feed, or a plain
		// string elsewhere. Normalize to the status name for pipeline pin classification.
		this.orderStatusName =
			data.order_status?.status ??
			(typeof data.order_status === 'string' ? data.order_status : undefined) ??
			data.orderStatusName ??
			data.orderStatus ??
			'';
		this.viewedBy = data.viewed_by ?? data.viewedBy ?? null;
		this.dispatchTrackerStatus = data.dispatchTrackerStatus?.name || data.dispatch_tracker_status?.name || null;
		// DOVE-2330 — the dispatch feeds ship the status relation ({id, name}); keep the id so the
		// dispatcher board can gate on DispatchTrackerStatus values instead of label strings.
		this.dispatchTrackerStatusId =
			data.dispatchTrackerStatus?.id ??
			data.dispatch_tracker_status?.id ??
			data.dispatch_tracker_status_id ??
			null;
		this.dispatchTrackerStatusHistory = data.dispatch_tracker_status_history || [];
		this.assignment = data.driver_truck_assignment ? data.driver_truck_assignment.id : data.assignment;
		this.driverName = data.driver_truck_assignment
			? data.driver_truck_assignment.related_user.fullname ||
				`${data.driver_truck_assignment.related_user.firstname} ${data.driver_truck_assignment.related_user.lastname}`
			: data.driverName || data.driver_name;
		this.driverNameIni = data.driver_name
			?.trim()
			.split(' ')
			.map((n: string) => n.charAt(0))
			.join('');
		// this.driverNameIni = data.driverNameIni || `${data.driver_name?.trim()}`
		this.insuranceName = data.insuranceName || data.insurance_name || '';
		this.currentAddress = data.currentAddress || data.current_address || '';
		this.currentFullAddress = this.getFullAddress(data);
		this.currentCity = data.currentCity || data.current_city || data.address?.city || '';
		this.currentCounty = data.currentCounty || data.current_county || data.address?.county || '';
		this.currentState = data.currentState || data.current_state || data.address?.state || '';
		this.currentZipcode = data.currentZipcode || data.current_zipcode || data.address?.zipcode || '';
		this.currentLatLong =
			data.currentLatLong ||
			data.current_lat_long ||
			(data.address?.latitude != null && data.address?.longitude != null
				? `${data.address.latitude},${data.address.longitude}`
				: '');
		this.createdByCustomer = data.createdByCustomer || data.created_by_customer || null;
		this.orderItemList =
			data.orderItemList ||
			data.order_items_list?.map((l: any) => ({
				orderDetailId: l.order_detail_id,
				thumbnail: l.image_thumbnail,
				imgMid: l.image_mid_size,
				imgFull: l.image_full_size,
				displayProductCategoryName: l.display_product_category_name,
				displayProductName: l.display_product_name,
				quantity: l.quantity,
				dateReceived: l.order_item?.date_received,
				itemMasterId: l.item_master_id
			}));
		this.deliveryWindowId = data.delivery_window_id;
		this.customDeliveryWindowStart = data.custom_delivery_window_start || null;
		this.customDeliveryWindowEnd = data.custom_delivery_window_end || null;

		if (this.customDeliveryWindowStart && this.customDeliveryWindowEnd) {
			this.deliveryWindow = `${this.customDeliveryWindowStart} - ${this.customDeliveryWindowEnd}`;
		} else {
			this.deliveryWindow = data.order_window?.window || 'Anytime';
		}
		this.isTransferOrder = data.isTransferOrder || false;
		this.leg = data.leg || undefined;
		this.legKey = data.legKey || data.leg_key || undefined;
		this.legBranchId = data.legBranchId ?? data.leg_branch_id ?? null;
		this.legBranchName = data.legBranchName || data.leg_branch_name || '';
		this.eta = data.eta
			? {
					etaMinutes: data.eta.eta_minutes,
					etaArrivalAt: data.eta.eta_arrival_at,
					distanceMeters: data.eta.distance_meters ?? null,
					updatedAt: data.eta.updated_at
				}
			: null;
		this.return = data.return ? new DispatchOrderReturn(data.return) : null;
	}

	private getFullAddress(data: any): string {
		if (data.currentFullAddress) {
			return data.currentFullAddress.trim();
		}
		if (data.address) {
			const parts = [
				data.address.address_1,
				data.address.address_2,
				data.address.city,
				data.address.state,
				data.address.zipcode
			];
			return parts.filter((p) => p).join(', ');
		}
		if (data.current_full_address) {
			return data.current_full_address.trim();
		}
		return '';
	}

	toOriginalFormat(): { [key: string]: any } {
		return {
			order_id: this.orderId, // Not in orderBe
			patient_id: this.patientId,
			order_flag_name: this.orderFlagName, // Not in orderBe
			order_flag_icon: this.orderFlagIcon, // Not in orderBe
			order_flag_id: this.orderFlagId, // Not in orderBe
			branch_name: this.branchName, // Not in orderBe
			patient_name: this.patientName, // Not in orderBe
			patient_active: this.patientActive, // Not in orderBe
			//>>>>Temporary removed for find usage: scheduled_date: this.orderDate.toISOString().slice(0, 19).replace('T', ' '),
			order_type: `${this.orderType} Ticket`, // Not in orderBe
			order_status: this.orderStatus, // Not in orderBe
			assignment: this.assignment, // Not in orderBe
			driver_name: this.driverName, // Not in orderBe
			insurance_name: this.insuranceName, // Not in orderBe
			current_address: this.currentAddress, // Not in orderBe
			current_city: this.currentCity, // Not in orderBe
			current_county: this.currentCounty, // Not in orderBe
			current_state: this.currentState, // Not in orderBe
			current_zipcode: this.currentZipcode, // Not in orderBe
			current_full_address: this.currentFullAddress, // Not in orderBe
			current_lat_long: this.currentLatLong, // Not in orderBe
			created_by_customer: this.createdByCustomer, // Not in orderBe
			order_items_list: this.orderItemList?.map((l: any) => ({
				order_detail_id: l.orderDetailId, // Not in orderBe
				image_thumbnail: l.thumbnail, // Not in orderBe
				image_mid_size: l.imgMid, // Not in orderBe
				image_full_size: l.imgFull, // Not in orderBe
				display_product_category_name: l.displayProductCategoryName, // Not in orderBe
				display_product_name: l.displayProductName, // Not in orderBe
				quantity: l.quantity, // Not in orderBe
				date_received: l.dateReceived, // Not in orderBe
				item_master_id: l.itemMasterId // Not in orderBe
			})),
			delivery_window_id: this.deliveryWindowId,
			delivery_window: this.deliveryWindow, // Not in orderBe
			custom_delivery_window_start: this.customDeliveryWindowStart,
			custom_delivery_window_end: this.customDeliveryWindowEnd
		};
	}
}

export interface IOrderType {
	id: number;
	type: string;
}
export class OrderType implements IOrderType {
	id: number;
	type: string;

	constructor(data: any) {
		this.id = data.id;
		this.type = data.type;
	}
}

export interface IPatientDetail {
	patientId: number;
	patientFullName: string;
	/** DOVE-2476 — Active / Inactive / Pending / Voided, from Patient::status() on the API. */
	patientStatus: string | null;
	streetAddress: string | null;
	addressType: string | null;
	addressUse: AddressUse | null;
	location: string | null;
	patientBranch: string | null;
	phoneNumber: string | null;
	phoneType: string | null;
	phoneDescription: string | null;
	source: string | null;
	insurance_id: number | null;
	// insuranceName: string | null;
	insurance: string | null;
	patientInfectious: string;
	infectiousDetails: string | null;
}

export enum DispatchTrackerStatus {
	Scheduled = 1,
	OnBoard = 2,
	OnRoute = 3,
	InRoute = 4,
	InProgress = 5,
	Completed = 6,
	Confirmed = 7
}

/**
 * DOVE-2367 — DTS options for search filters, in the enum's own order (the dispatch
 * lifecycle), not alphabetical.
 *
 * The labels must match what the search results render — `DispatchTrackerStatus::getDisplayName()`
 * on the backend, passed through `dtsDisplayLabel()` (DOVE-2404: Scheduled → Not Ready,
 * On-Board → Ready) — or the filter and the row would show different spellings. There is no DTS dropdown endpoint; the enum is already
 * mirrored above, so a constant beside it beats a round trip for seven fixed values.
 */
export const DISPATCH_TRACKER_STATUS_OPTIONS: { name: string; code: number }[] = [
	{ name: 'Not Ready', code: DispatchTrackerStatus.Scheduled },
	{ name: 'Ready', code: DispatchTrackerStatus.OnBoard },
	{ name: 'On-Route', code: DispatchTrackerStatus.OnRoute },
	{ name: 'In-Route', code: DispatchTrackerStatus.InRoute },
	{ name: 'In-Progress', code: DispatchTrackerStatus.InProgress },
	{ name: 'Completed', code: DispatchTrackerStatus.Completed },
	{ name: 'Confirmed', code: DispatchTrackerStatus.Confirmed }
];

/**
 * DOVE-2330 — dispatch tracker statuses at which the office can no longer edit an order.
 * The freeze starts the moment the driver taps "On the Way" (In-Route), not "Start"
 * (In-Progress), because the driver already holds the frozen line list from In-Route on.
 *
 * Membership check only — never `>= InRoute`, which would sweep in Completed/Confirmed and
 * break Confirm/Reopen/Void.
 *
 * Prefer the backend-computed `dispatch.dts_locked` flag (`OrderDetail.isDtsLocked`) wherever
 * the full order payload is available; this list exists for the dispatcher board, whose list
 * payload carries only the dispatch tracker status.
 */
export const LOCKED_DISPATCH_TRACKER_STATUSES: number[] = [
	DispatchTrackerStatus.InRoute,
	DispatchTrackerStatus.InProgress
];

/**
 * DOVE-2330 — shared prefix of the backend's locked-order 422 messages. The backend sends a
 * state-specific sentence (In-Route: "...the driver is on the way. Contact the driver, or
 * cancel the order."; In-Progress: "...the driver has started it. Contact the driver.") and
 * exposes this prefix as its own constant. Match on the PREFIX only — never on a full
 * sentence, or a copy tweak on either side silently kills the force-reload.
 */
export const ORDER_LOCKED_MESSAGE_PREFIX = 'This order is locked because the driver';

/**
 * DOVE-2330 — banner copy of last resort, used ONLY when the order is locked but the backend
 * sent no `dts_locked_message` (a backend that predates the field, or the status-id
 * fail-closed path, where no message exists to send).
 *
 * Deliberately state-NEUTRAL: it must read true at both In-Route ("on the way") and
 * In-Progress ("started"), which is why it says neither. Never grow this into a per-state
 * sentence and never pick between variants from a status id — the resolved copy is the
 * backend's to own, and duplicating it across repos is the bug this replaced.
 */
export const ORDER_LOCKED_FALLBACK_MESSAGE =
	'This order is locked because the driver already has it. Contact the driver.';

/**
 * DOVE-2330 — builds a lock banner: the backend's state-correct sentence (or the neutral
 * fallback) plus the calling screen's own short clause naming what that screen disables.
 *
 * The split is deliberate. The WHY is state-dependent and belongs to the backend; the WHAT IS
 * DISABLED HERE is a property of the surface, so it lives next to the surface and is passed in.
 * `surfaceClause` must therefore be a fixed, complete sentence that reads correctly after any
 * of the base sentences — it must NEVER mention the dispatch state ("on the way", "started")
 * or be chosen from a status id. If a surface ever needs state to phrase its clause, that is a
 * signal to change the backend message, not to branch here.
 *
 * Composition lives only in this function so the join rule is not copy-pasted per surface.
 */
export function composeDtsLockBannerMessage(order: OrderDetail | null | undefined, surfaceClause: string): string {
	const baseMessage = order?.dtsLockBannerMessage ?? ORDER_LOCKED_FALLBACK_MESSAGE;
	return `${baseMessage} ${surfaceClause}`;
}

/** DOVE-2330 — true when the given dispatch tracker status id freezes office edits. */
export function isDispatchTrackerLocked(dispatchTrackerStatusId: number | null | undefined): boolean {
	return dispatchTrackerStatusId != null && LOCKED_DISPATCH_TRACKER_STATUSES.includes(dispatchTrackerStatusId);
}

export interface IDispatchDetail {
	orderPriorityId: number | null;
	orderPriority: string | null;
	dispatcher: string | null;
	dispatcherPhoneNumber: string | null;
	orderWindow: string | null;
	dispatchTracker: string | null;
	dispatchTrackerStatusId: number | null;
	driverTruckId: number | null;
	assignedTo: string | null;
	customDeliveryWindowStart: string | null;
	customDeliveryWindowEnd: string | null;
}

export interface IDispatchTrackerStatusHistory {
	id: number;
	name: string;
	timestamp: string | null;
	created_by?: {
		id: number;
		name: string;
	} | null;
	current: boolean;
}

export interface IAssignmentDetail {
	id: number;
	userId: number;
}

export interface IUser {
	id: number;
	name: string;
	email: string;
	branchId: number;
	branch: string;
}

export interface IAvailableDriver {
	value: number;
	label: string;
	userId: number;
	driverTruckId: number;
	truckId: number;
	truckName: string;
	branchId: number;
	branchName: string;
}
/** DOVE-2667 item exceptions — the order a follow-up order was created from (`source_order`). */
export interface IRelatedSourceOrder {
	id: number;
	type: string | null;
	statusLabel: string | null;
}

export class RelatedSourceOrder implements IRelatedSourceOrder {
	id: number;
	type: string | null;
	statusLabel: string | null;

	constructor(data: any) {
		this.id = data.id;
		this.type = data.type ?? null;
		this.statusLabel = data.status_label ?? null;
	}
}

/** DOVE-2667 item exceptions — an order created from this order's exception lines (`follow_up_orders`). */
export interface IFollowUpOrderSummary {
	id: number;
	/** Branch-local `Y-m-d`. */
	scheduledDate: string | null;
	statusLabel: string | null;
	/** Item descriptions. */
	items: string[];
}

export class FollowUpOrderSummary implements IFollowUpOrderSummary {
	id: number;
	scheduledDate: string | null;
	statusLabel: string | null;
	items: string[];

	constructor(data: any) {
		this.id = data.id;
		this.scheduledDate = data.scheduled_date ?? null;
		this.statusLabel = data.status_label ?? null;
		this.items = Array.isArray(data.items) ? data.items : [];
	}
}

export interface IOrderDetail {
	// Basic order info
	orderId: number;
	branchId: number;
	branchName: string;
	insuranceId: number;
	insuranceName: string | null;
	insuranceTeam: string | null;
	orderDate: Date | null;
	orderType: string | null;
	orderTypeId: number;
	orderStatus: string | null;

	// Order Details section
	scheduledDate: Date | null;
	scheduledDateFormatted: string | null;
	completed: string;
	completedAt: Date | null;
	completedBy: IUser | null;
	signedBy: string | null;
	signedByRelationship: string | null;
	confirmed: string;
	confirmedAt: Date | null;
	confirmedBy: IUser | null;
	comments: string | null;
	customerComments: string | null;
	orderReason: string | null;
	orderedBy: string | null;
	orderedByEmail: string | null;
	orderSource: string | null;
	approvedBy: string | null;
	approvedOn: Date | null;
	onCall: string;
	onCallTime: string | null;

	// DOVE-2330 — backend-computed office-edit lock (OrderShowResource `dispatch.dts_locked`).
	// True = edits frozen. It encodes the DTS lock ONLY; an endpoint can still refuse on
	// order-status grounds (e.g. a Confirmed order) with this false.
	isDtsLocked: boolean;

	// DOVE-2330 — backend-resolved lock copy (`dispatch.dts_locked_message`); null whenever
	// the order is not locked. Render it; never assemble it here.
	dtsLockedMessage: string | null;

	// Grouped sections
	patientDetail: IPatientDetail;
	dispatchDetail: IDispatchDetail;
	assignmentDetail: IAssignmentDetail;
	dispatchTrackerStatusHistory: IDispatchTrackerStatusHistory[];
}

export class OrderDetail implements IOrderDetail {
	// Basic order info
	orderId: number;
	branchId: number;
	branchName: string;
	insuranceId: number;
	insuranceName: string | null;
	insuranceTeam: string | null;
	orderDate: Date | null;
	orderType: string | null;
	orderTypeId: number;
	orderStatus: string | null;
	orderStatusId: number | null;

	// Order Details section
	scheduledDate: Date | null;
	scheduledDateFormatted: string | null;
	completed: string;
	completedAt: Date | null;
	completedBy: IUser | null;
	signedBy: string | null;
	signedByRelationship: string | null;
	confirmed: string;
	confirmedAt: Date | null;
	confirmedBy: IUser | null;
	comments: string | null;
	customerComments: string | null;
	orderReason: string | null;
	orderReasonId: number | null;
	orderedBy: string | null;
	orderedByUserId: number | null;
	orderedByEmail: string | null;
	orderSource: string | null;
	approvedBy: string | null;
	approvedOn: Date | null;
	onCall: string;
	onCallTime: string | null;

	// DOVE-2330 — computed by OrderShowResource (`dispatch.dts_locked`); true once the driver
	// is on the way (In-Route) or has started (In-Progress).
	isDtsLocked: boolean;

	// DOVE-2330 — the backend's state-correct lock sentence, or null when not locked.
	// Read `dtsLockBannerMessage` for display; it applies the neutral fallback.
	dtsLockedMessage: string | null;

	// DOVE-2110 — Send Update button visibility (computed by OrderShowResource)
	isOnlineOrder: boolean;
	insuranceHasOnlineUpdatesContact: boolean;
	// DOVE-2241 — insurance requires order entry in a third party system before confirming
	insuranceThirdPartyConfirmationRequired: boolean;
	// DOVE-2112 — recipient (ordered-by user) resolvable for an applicable source.
	orderedByRecipientAvailable: boolean;
	// DOVE-2110 — embedded mailto payload; null when the button shouldn't act.
	sendUpdatePayload: { to: string[]; subject: string } | null;

	// DOVE-2667 item exceptions — related orders (OrderShowResource `source_order` / `follow_up_orders`).
	sourceOrder: RelatedSourceOrder | null;
	followUpOrders: FollowUpOrderSummary[];

	// New patient location properties from root level
	patientLocationHistoryId: number | null;
	patientLocation: any | null;
	patientLocations: PatientLocationHistory[] | null;

	// Patient phone properties
	phoneId: number | null;
	patientPhones: PatientPhone[] | null;
	// DOVE-2477 — the order's own phone, resolved by the BE. Still present after the number
	// was soft-deleted from the patient profile (and so missing from `patientPhones`).
	phone: PatientPhone | null;
	phoneDeleted: boolean;

	patientDetail: IPatientDetail;
	dispatchDetail: IDispatchDetail;
	assignmentDetail: IAssignmentDetail;
	dispatchTrackerStatusHistory: IDispatchTrackerStatusHistory[];

	constructor(data: any) {
		// Basic order info
		this.orderId = data.id;
		this.branchId = data.branch_id || data.branchId || 0;
		this.branchName = data.branch_name || data.branchName || data.branch?.name || data.branch?.branch_name || '';
		this.insuranceId = data.insurance_id || data.insuranceId || 0;
		this.insuranceName = data.insurance_name || data.insuranceName || null;
		this.insuranceTeam = data.insurance_team || null;
		this.orderDate = data.scheduled_at ? new Date(data.scheduled_at) : null;
		this.orderTypeId = data.order_type_id || data.orderTypeId || null;
		this.orderType = data.order_type || data.orderType || null;
		this.orderStatus = data.order_status ? data.order_status : null;
		this.orderStatusId = data.order_status_id || null;
		this.scheduledDate = data.scheduled_at ? new Date(data.scheduled_at) : null;
		this.scheduledDateFormatted = data.scheduled_at ? this.formatDateString(data.scheduled_at) : null;
		this.completed = data.completed_at ? 'Yes' : 'No';
		this.completedAt = data.completed_at_iso ? new Date(data.completed_at_iso) : null;
		this.confirmedAt = data.confirmed_at_iso ? new Date(data.confirmed_at_iso) : null;
		this.completedBy = data.completed_by
			? {
					id: data.completed_by.id,
					name: data.completed_by.name,
					email: data.completed_by.email,
					branchId: data.completed_by.branch_id,
					branch: data.completed_by.branch
				}
			: null;
		this.signedBy = data.signed_by;
		this.signedByRelationship = data.signed_by_relationship ?? null;
		this.confirmed = data.confirmed_at ? 'Yes' : 'No';
		this.confirmedBy = data.confirmed_by;
		this.comments = data.comment;
		this.customerComments = data.customer_comment;
		this.orderReason = data.order_reason;
		this.orderReasonId = data.order_reason_id ?? null;
		this.orderedBy = data.ordered_by;
		this.orderedByUserId = data.ordered_by_user_id ?? null;
		// DOVE-2112 — prefer the resolved ordered-by-user email; fall back to legacy importer.
		this.orderedByEmail = data.ordered_by_email ?? data.imported_by?.email ?? null;
		this.orderSource = data.order_source;
		this.approvedBy = data.insuranceApprover;
		// DOVE-2508: read the ISO key, like completedAt/confirmedAt above. The plain
		// insurance_approved_at is a naive 'Y-m-d H:i:s' the browser parses in its own timezone,
		// so an approval stamped late in the day rendered as the next day. Falls back to the old
		// key so the profile still shows a date against an API that predates the ISO field.
		this.approvedOn = data.insurance_approved_at_iso
			? new Date(data.insurance_approved_at_iso)
			: data.insurance_approved_at
				? new Date(data.insurance_approved_at)
				: null;
		this.onCall = data.on_call ? 'Yes' : 'No';
		this.onCallTime = data.on_call_received_time;

		// DOVE-2110 — Send Update button visibility booleans from OrderShowResource
		this.isOnlineOrder = data.is_online_order ?? false;
		this.insuranceHasOnlineUpdatesContact = data.insurance_has_online_updates_contact ?? false;
		this.insuranceThirdPartyConfirmationRequired = data.insurance_third_party_confirmation_required ?? false;
		// DOVE-2112 — ordered-by recipient resolvable (applicable source + email present).
		this.orderedByRecipientAvailable = data.ordered_by_recipient_available ?? false;
		// DOVE-2110 — embedded Send-Update mailto payload (null when no action).
		this.sendUpdatePayload = data.send_update_payload ?? null;

		// DOVE-2667 item exceptions — "Created from order #X" / "Related order".
		this.sourceOrder = data.source_order ? new RelatedSourceOrder(data.source_order) : null;
		this.followUpOrders = (Array.isArray(data.follow_up_orders) ? data.follow_up_orders : []).map(
			(order: any) => new FollowUpOrderSummary(order)
		);

		// DOVE-1811 redesign — infectious_disease is now a per-order field.
		// Read the order's own value, not the patient's (which is only a mirror).
		let patientInfectious: string;
		let infectiousDetails: string | null;
		if (data.infectious_disease) {
			patientInfectious = 'Yes';
			infectiousDetails = data.infectious_disease;
		} else {
			patientInfectious = 'No';
			infectiousDetails = null;
		}

		// Get the current patient location for address type and location name
		// BE: PatientLocationHistoryResource => PatientResource => OrderShowResource
		const currentLocation =
			data.patient_detail?.patient_locations?.find((loc: any) => loc.id === data.patient_location_history_id) ||
			data.patient_detail?.patient_locations?.find((loc: any) => loc.is_active === true);
		// DOVE-2464: patient_location_type is always present, but its name comes from a nullable
		// FK that almost no row sets, so Address Type rendered '-' on nearly every order. Derive
		// it from the facility the way the patient endpoint does server-side
		// (PatientFindResource: name ?? (facility_id ? 'Facility' : 'Home')); the facility key is
		// only emitted when facility_id is set, so its presence is the same signal.
		const baseAddressType = currentLocation
			? currentLocation.patient_location_type?.name || (currentLocation.facility ? 'Facility' : 'Home')
			: null;

		// DOVE-2464 QA — for a facility address, say which kind: "Facility (SNF)". The value is
		// already on the payload: PatientLocationHistoryResource emits facility_type whenever
		// facility_id is set, both at the top level and nested under facility. Gated on the same
		// facility signal that resolves the base type, and on the type itself being present, so a
		// home address stays "Home" and a facility with no type never renders "Facility ()".
		const facilityType = (currentLocation?.facility_type || currentLocation?.facility?.facility_type)?.trim();
		const addressType =
			baseAddressType && currentLocation?.facility && facilityType
				? `${baseAddressType} (${facilityType})`
				: baseAddressType;

		// Show facility name for facility addresses; null falls through to '-' in the template
		const location: string | null = currentLocation?.facility?.facility_name?.trim() || null;

		// Process patient location data
		this.patientLocationHistoryId = data.patient_location_history_id || null;
		this.patientLocation = data.patient_location || null;
		this.patientLocations = data.patient_locations
			? data.patient_locations.map((locationData: any) => new PatientLocationHistory(locationData))
			: null;

		// Process patient phone data
		this.phoneId = data.phone_id || null;
		this.patientPhones = data.patient_detail?.phones
			? data.patient_detail.phones.map((phoneData: any) => new PatientPhone(phoneData))
			: null;

		// DOVE-2477 — the BE resolves the order's phone itself so a soft-deleted number still
		// shows the number the order was actually placed with. The client-side lookup below is
		// only a fallback for payloads predating `phone`.
		this.phone = data.phone ? new PatientPhone(data.phone) : null;
		this.phoneDeleted = !!data.phone?.deleted;

		// Find the selected phone based on phone_id, with fallback to primary/first phone
		const selectedPhone =
			this.phone ||
			this.patientPhones?.find((phone) => phone.id === this.phoneId) ||
			this.patientPhones?.find((phone) => phone.isPrimary) ||
			this.patientPhones?.[0] ||
			null;

		// `phone` carries no source, so read it off the matching profile row — which is gone
		// once the number is soft-deleted, leaving Source blank rather than wrong.
		const selectedPhoneSource = this.patientPhones?.find((phone) => phone.id === selectedPhone?.id)?.source;

		// Build street address - check patient_location first, then fall back to patient_address
		let streetAddress = null;
		if (this.patientLocation) {
			const parts = [];

			if (this.patientLocation.address_1) {
				parts.push(this.patientLocation.address_1);
			}

			if (this.patientLocation.address_2) {
				parts.push(this.patientLocation.address_2);
			}

			if (this.patientLocation.city) {
				parts.push(this.patientLocation.city);
			}

			if (this.patientLocation.state) {
				parts.push(this.patientLocation.state);
			}

			if (this.patientLocation.zipcode) {
				parts.push(this.patientLocation.zipcode);
			}

			streetAddress = parts.join(', ');
		} else if (data.patient_address) {
			streetAddress = [
				data.patient_address.address_1,
				data.patient_address.address_2,
				data.patient_address.city,
				data.patient_address.state,
				data.patient_address.zipcode
			]
				.filter(Boolean)
				.join(', ');
		}

		this.patientDetail = {
			patientId: data.patient_id,
			patientFullName: data.patient_full_name,
			patientStatus: data.patient_detail?.status || null,
			streetAddress: streetAddress,
			addressType: addressType,
			addressUse: (currentLocation?.use as AddressUse) || null,
			location: location,
			patientBranch: data.patient_detail?.branch || null,
			phoneNumber: selectedPhone?.number || null,
			phoneType: selectedPhone?.type || null,
			phoneDescription: selectedPhone?.description || null,
			source: selectedPhoneSource || null,
			insurance_id: data.patient_detail?.insurance_id || null,
			insurance: data.patient_detail?.insurance || null,
			// insuranceName: data.patient_detail?.insurance || null,
			patientInfectious,
			infectiousDetails
		};

		if (data.dispatch) {
			// Check for custom delivery window from either nested dispatch or root level
			const customStart = data.dispatch?.custom_delivery_window_start || data.custom_delivery_window_start;
			const customEnd = data.dispatch?.custom_delivery_window_end || data.custom_delivery_window_end;

			let orderWindow: string;
			if (customStart && customEnd) {
				orderWindow = `${customStart} - ${customEnd}`;
			} else {
				orderWindow = 'Anytime';
			}

			this.dispatchDetail = {
				orderPriorityId: data.dispatch?.order_priority_id || null,
				orderPriority: data.dispatch?.order_priority || null,
				dispatcher: data.dispatch?.dispatcher?.name || null,
				dispatcherPhoneNumber: data.dispatch?.dispatcher?.phone_number || null,
				orderWindow,
				dispatchTracker: data.dispatch?.dispatch_tracker_status || null,
				dispatchTrackerStatusId: data.dispatch?.dispatch_tracker_status_id ?? null,
				// `dispatch.assignment` is the DriverTruckAssignment record; its `id` is the FK
				// stored as `driver_truck_id` on the order. When unassigned, assignment is null.
				// Anchor on the assignment relation only — never trust a top-level FK that may
				// be stale on a previously-assigned order. Treat `id: 0`/null/undefined as no driver.
				driverTruckId: data.dispatch?.assignment?.id ? data.dispatch.assignment.id : null,
				assignedTo: data.dispatch?.driver_name?.trim() || null,
				customDeliveryWindowStart: customStart || null,
				customDeliveryWindowEnd: customEnd || null
			};

			this.assignmentDetail = {
				id: data.dispatch.assignment?.id || 0,
				userId: data.dispatch.assignment?.user_id || 0
			};
		} else {
			this.dispatchDetail = {
				orderPriorityId: null,
				orderPriority: null,
				dispatcher: null,
				dispatcherPhoneNumber: null,
				orderWindow: 'Anytime',
				dispatchTracker: null,
				dispatchTrackerStatusId: null,
				driverTruckId: null,
				assignedTo: null,
				customDeliveryWindowStart: null,
				customDeliveryWindowEnd: null
			};

			this.assignmentDetail = {
				id: 0,
				userId: 0
			};
		}

		this.dispatchTrackerStatusHistory =
			data.dispatch_tracker_status_history || data.dispatch?.dispatch_tracker_status_history || [];

		// DOVE-2330 — the office-edit lock is owned by the backend and carried as
		// `dispatch.dts_locked` (TRUE = edits frozen), so moving the threshold again is a
		// backend-only change. The nested key is the contract; the top-level read is a cheap
		// tolerance, and the status-id derivation only covers a payload that predates the flag.
		// Do not derive the lock from status ids anywhere else.
		//
		// This resolves in three distinct cases, and they must NOT be collapsed:
		//   1. `dispatch` absent/null entirely → we were handed a payload that cannot tell us
		//      anything about the lock. Resolve to LOCKED. Rendering the whole order as freely
		//      editable and letting every save 422 is the worse failure.
		//   2. `dispatch` present with a boolean `dts_locked` → use it verbatim. This is the
		//      real path; the flag is already in "locked" polarity, so there is nothing to
		//      invert here (the predecessor `can_edit` was the inverse — do not reintroduce it).
		//   3. `dispatch` present but `dts_locked` absent → fall back to the status id.
		//      A null `dispatch_tracker_status_id` means the order has no dispatch tracker
		//      status yet (never dispatched), which is NOT locked — the office must still be
		//      able to edit it. Only In-Route/In-Progress lock.
		const hasDispatchPayload = data.dispatch !== undefined && data.dispatch !== null;
		const dtsLockedFlag = data.dispatch?.dts_locked ?? data.dts_locked;
		if (!hasDispatchPayload && typeof dtsLockedFlag !== 'boolean') {
			this.isDtsLocked = true;
		} else if (typeof dtsLockedFlag === 'boolean') {
			this.isDtsLocked = dtsLockedFlag;
		} else {
			this.isDtsLocked = isDispatchTrackerLocked(this.dispatchDetail.dispatchTrackerStatusId);
		}

		// DOVE-2330 — the resolved lock sentence rides along with the flag. It is null whenever
		// the order is not locked, and it is also absent on the two paths above that resolve the
		// lock without a backend message (older payload, status-id fail-closed) — hence the
		// fallback in `dtsLockBannerMessage`. Blank strings are normalised to null so the
		// fallback wins instead of rendering an empty banner.
		const lockedMessage = data.dispatch?.dts_locked_message ?? data.dts_locked_message;
		this.dtsLockedMessage =
			typeof lockedMessage === 'string' && lockedMessage.trim() !== '' ? lockedMessage : null;
	}

	/**
	 * DOVE-2330 — what the lock banners render. The backend owns the state-correct copy
	 * (In-Route vs In-Progress); this only chooses between "what the backend said" and the
	 * state-neutral fallback. Do NOT branch on status ids here to pick a sentence.
	 */
	get dtsLockBannerMessage(): string {
		return this.dtsLockedMessage ?? ORDER_LOCKED_FALLBACK_MESSAGE;
	}

	private formatDateString(raw: string): string | null {
		const datePart = raw.split('T')[0].split(' ')[0]; // handles both "YYYY-MM-DD HH:mm:ss" and ISO
		const [year, month, day] = datePart.split('-');
		if (!year || !month || !day) return null;
		return `${month}/${day}/${year}`;
	}
}

export interface IOrderItemDetail {
	orderDetailId: number;
	delivered: number;
	username: string;
	orderId: number;
	assetId: number | null;
	itemMasterId: number;
	itemType: {
		value: number | null;
		name: string | null;
	};
	orderItemStatus: string | null;
	orderItemStatusId: number | null;
	imgThumbnail: string;
	imgMid: string;
	imgFull: string;
	displayProductName: string;
	displayProductCategoryName: string;
	displayProductCategoryShortDescription: string;
	itemMasterDescription: string;
	itemDetailId: number | null;
	createdAt: string;
	quantity: number;
	dateReceived: string;
	lineGroupId?: number;
	lineRole?: string;
	// DOVE-2062 R2 — the order_detail_status_id as loaded from the API, kept
	// stable even after the OIS dropdown two-way binds a new value. Used to
	// detect a recovery line (RecoveryScheduled = 21).
	loadedStatusId?: number | null;
	// DOVE-2062 R2 — item AMPS situation (invoiced / writeOff / soldMia / ...).
	// Drives the Recovery-Failed-only exception filter on disposed recovery
	// lines. BACKEND FOLLOW-UP: not yet present on the confirm/completion
	// order-detail payload — the filter stays inert until backend supplies it.
	itemSituation?: string | null;
	// DOVE-2428 — true when the item master is flagged as a Next Day item, so the
	// order items tables can show the same badge the Add Items modal already does.
	nextDayItem?: boolean;
	// DOVE-2667 item exceptions 01 — the driver's comment on an item exception, shown under the
	// exception status. Internal only (not exposed on the client portal).
	exceptionComment?: string | null;
	// DOVE-2410 — Rx settings for a line added on the Confirming page; sent with confirm-order.
	rxData?: CartRxData;
}

/**
 * DOVE-1945 — response of GET /orders/{order}/confirm-asset-lookup.
 * item_type is the ItemType enum value: Unique = 1, Generic = 2.
 */
export interface IConfirmAssetLookup {
	id: number;
	item_master_id: number;
	item_master_name: string;
	item_type: number;
	// DOVE-1991 — optional category label for the fallback (no-existing-line) office-add.
	// Backend to add this field to the confirm-asset-lookup response (e.g. "Oxygen").
	item_master_category?: string | null;
}

/**
 * DOVE-2074 — response of POST /orders/{order}/validate-confirming-line.
 * Single-line AMPS dry-run validation result.
 *  - valid:true            → pass or N/A (no movement / generic / null rule)
 *  - valid:false amps_invalid → AMPS rejected (message = friendly summary)
 *  - valid:false not_found    → asset doesn't exist (HTTP 404)
 *
 * DOVE-2363 — third outcome: valid:true amps_dirty_delivery → allowed, but the asset is
 * Dirty and the office has to be told (warning only, never blocks Confirm).
 */
export interface IValidateConfirmingLineResponse {
	valid: boolean;
	code?: 'amps_invalid' | 'not_found' | 'amps_dirty_delivery';
	message?: string;
}

export interface IValidateConfirmingLineRequest {
	item_detail_id: number;
	item_master_id?: number;
	order_detail_status_id: number;
	completed_at: string | null;
}

/**
 * DOVE-2457 — PATCH /orders/{order}/completed-at, the office edit of the completed date on an
 * already-Completed order. Independent of the confirm payload.
 *
 * `documents_stale` is the backend telling us whether any PDF was already generated against the
 * old date; those are never regenerated, so the toast has to say so.
 */
export interface IUpdateCompletedAtResponse {
	message: string;
	completed_at: string;
	movements_redated: number;
	documents_stale: boolean;
}

/**
 * DOVE-2457 — one entry of the 422 `conflicts` list. `item_detail_id: null` means the conflict is
 * about the order as a whole (billing period) rather than one asset. `message` is written
 * server-side and rendered verbatim; `code` is for logging/tests and is never displayed.
 */
export interface ICompletedAtConflict {
	item_detail_id: number | null;
	asset_id: string | null;
	item: string | null;
	code: string;
	message: string;
}

/**
 * DOVE-2362 — response of GET /orders/{order}/confirming-media: the driver's per-line scan
 * photos plus the signed completion PDF. Raw snake_case, same as the confirming-page DTOs
 * above. `captured_at` / `generated_at` are ISO8601 UTC — render through the date pipe so
 * the confirmer sees local time.
 */
export interface IConfirmingMediaPhoto {
	document_id: number;
	order_detail_id: number;
	item_detail_id: number | null;
	file_name: string;
	captured_at: string;
	mime_type: string;
}

/**
 * What the shared photo thumb / viewer read: a driver scan photo, or any other photo (e.g. the
 * DOVE-2667 Refuse / Reschedule photo) given a file name and a capture time.
 */
export type IViewablePhoto = Pick<IConfirmingMediaPhoto, 'file_name' | 'captured_at'> &
	Partial<Pick<IConfirmingMediaPhoto, 'document_id' | 'item_detail_id'>>;

export interface IConfirmingSignedDocument {
	document_id: number;
	file_name: string;
	generated_at: string;
}

export interface IConfirmingMediaResponse {
	// Always an array; may be empty.
	photos: IConfirmingMediaPhoto[];
	// null when the order has no signed completion PDF.
	signed_document: IConfirmingSignedDocument | null;
	// DOVE-2537 — null when the order has no Home Inspection PDF.
	home_inspection_document?: IConfirmingSignedDocument | null;
}

export interface IExchangeGroup {
	lineGroupId: number;
	itemMasterId: number;
	displayProductName: string;
	displayProductCategoryName: string;
	quantity: number;
	items: IOrderItemDetail[];
}

export class OrderItem implements IOrderItemDetail {
	orderDetailId: number;
	delivered: number;
	username: string;
	orderId: number;
	assetId: number | null;
	itemMasterId: number;
	itemType: {
		value: number | null;
		name: string | null;
	};
	orderItemStatus: string | null;
	orderItemStatusId: number | null;
	imgThumbnail: string;
	imgMid: string;
	imgFull: string;
	displayProductName: string;
	displayProductCategoryName: string;
	displayProductCategoryShortDescription: string;
	itemMasterDescription: string;
	itemDetailId: number | null;
	createdAt: string;
	quantity: number;
	dateReceived: string;
	lineGroupId?: number;
	lineRole?: string;
	loadedStatusId?: number | null;
	itemSituation?: string | null;
	nextDayItem?: boolean;
	exceptionComment: string | null;

	constructor(data: any) {
		this.orderDetailId = data.order_detail_id || data.orderDetailId;
		this.delivered = data.delivered;
		this.username = data.username;
		this.orderId = data.order_id || data.orderId;
		this.assetId = data.item_detail_id || data.assetId || null;
		this.itemMasterId = data.item_master_id || data.itemMasterId;
		this.itemType = data.item_type || data.itemType;
		this.orderItemStatus = data.order_detail_status || data.orderItemStatus || null;
		this.orderItemStatusId = data.order_detail_status_id || data.orderItemStatusId || null;
		this.imgThumbnail = data.image_thumbnail || data.thumbnail;
		this.imgMid = data.image_mid_size || data.imgMid;
		this.imgFull = data.image_full_size || data.imgFull;
		this.displayProductName = data.display_product_name || data.displayProductName;
		this.displayProductCategoryName = data.display_product_category_name || data.displayProductCategoryName;
		this.displayProductCategoryShortDescription =
			data.display_product_category_short_description || data.displayProductCategoryShortDescription;
		this.itemMasterDescription = data.item_master_description || data.itemMasterDescription;
		this.itemDetailId = data.item_detail_id || data.itemDetailId;
		this.createdAt = data.created_at || data.createdAt;
		this.quantity = data.quantity;
		this.dateReceived = data.created_at || data.dateReceived; // Using created_at as date received
		this.lineGroupId = data.line_group_id || data.lineGroupId;
		this.lineRole = data.line_role || data.lineRole;
		this.loadedStatusId = data.order_detail_status_id ?? data.orderItemStatusId ?? null;
		// BACKEND FOLLOW-UP (DOVE-2062 R2): backend not yet sending item_situation.
		this.itemSituation = data.item_situation ?? data.itemSituation ?? null;
		// DOVE-2428 — item master flag, surfaced as the "Next Day Item" badge.
		this.nextDayItem = data.next_day_item === true || data.nextDayItem === true;
		// DOVE-2667 item exceptions 01 — driver comment on the exception (order_detail_status).
		this.exceptionComment = data.exception_comment ?? data.exceptionComment ?? null;
	}
}
